import { useQuery } from '@apollo/client';

import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import {
  GetSimilarJobsForUserDocument,
  GetJobRecommendationsDocument,
} from '@xing-com/crate-jobs-graphql';
import type { JobRecommendationFragment } from '@xing-com/crate-jobs-graphql';

import { useSimilarJobs } from './use-similar-jobs';

const LI_CONSUMER = 'loggedin.web.jobs.posting.similarjobsrecommendations';
const LO_CONSUMER = 'loggedout.web.jobs.posting.similarjobsrecommendations';

type SimilarJobsResponse = {
  loading: boolean;
  recommendations: (JobRecommendationFragment | null)[];
  similarJobs: (JobRecommendationFragment | null)[];
};

type Props = { limit: number; jobId?: string };

export const useSimilarJobsAndRecos = ({
  limit,
  jobId,
}: Props): SimilarJobsResponse => {
  const { isLoggedIn } = useLoginState();
  const { loading: similarJobsLoading, similarJobs } = useSimilarJobs({
    limit,
    jobId,
  });
  const { loading: jobRecosLoadingLoggedIn, data: dataLoggedIn } = useQuery(
    GetJobRecommendationsDocument,
    {
      variables: {
        consumer: LI_CONSUMER,
        first: limit,
      },
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'ignore',
      skip: !isLoggedIn,
    }
  );
  const recommendationsLoggedIn = (
    dataLoggedIn?.viewer?.jobRecommendations?.edges ?? []
  ).reduce<JobRecommendationFragment[]>(
    (prev, { node }) =>
      node.job?.__typename === 'VisibleJob' ? [...prev, node] : prev,
    []
  );
  const { loading: jobRecosLoadingLoggedOut, data: dataLoggedOut } = useQuery(
    GetSimilarJobsForUserDocument,
    {
      variables: {
        consumer: LO_CONSUMER,
        first: limit,
      },
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'ignore',
      skip: isLoggedIn,
    }
  );
  const recommendationsLoggedOut = (
    dataLoggedOut?.viewer?.similarJobsForUserRecommendations?.edges ?? []
  ).reduce<JobRecommendationFragment[]>(
    (prev, { node }) =>
      node.job?.__typename === 'VisibleJob' ? [...prev, node] : prev,
    []
  );
  const loading =
    similarJobsLoading || jobRecosLoadingLoggedOut || jobRecosLoadingLoggedIn;
  const recommendations =
    recommendationsLoggedIn.length > 0
      ? recommendationsLoggedIn
      : recommendationsLoggedOut;

  return {
    loading,
    recommendations,
    similarJobs,
  };
};
